'use client';

import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import Image from '@/components/Image';
import MagicJump, { type JumpTarget } from '@/components/MagicJump';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type {
  OneRowFourNavContent,
  OneRowFourNavItem,
  OneRowFourNavProps,
  OneRowFourNavTrackingParams,
} from './types';

const MAX_NAV_ITEMS = 4;
const LINE_BREAK_TAG_RE = /(?:<|&(?:amp;)*lt;)\s*br\s*\/?\s*(?:>|&(?:amp;)*gt;)/gi;

const formatText = (text?: string) => (text ?? '').replace(LINE_BREAK_TAG_RE, '\n');

function buildTrackingParams(
  item: OneRowFourNavItem,
  index: number,
  moduleIndex: number,
  indexData: OneRowFourNavContent &
    Pick<OneRowFourNavProps['indexData'], 'component_id' | 'blocks_advances_id'>,
): OneRowFourNavTrackingParams {
  return {
    module_index: moduleIndex,
    module_name: indexData.component_id,
    module_id: String(indexData.blocks_advances_id || ''),
    mkt_index: index,
    mkt_index_sub: 0,
    mkt_jump_type: String(item.jump_type),
    mkt_jump_content: String(item.Jump_target),
    module_tab_name: '',
    coupon_id_list: '',
    coupon_name_list: '',
    shop_sku: String(item.sku?.shop_sku || ''),
    business_sku: String(item.sku?.business_sku || ''),
    product_sku: String(item.sku?.sku || ''),
    product_id: String(item.sku?.id || ''),
    product_name: String(item.sku?.short_name || ''),
    group_id: String(item.product_group?.id || ''),
    group_name: String(item.product_group?.name || ''),
    module_title: '',
  };
}

function OneRowFourNavItemView({
  item,
  index,
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: OneRowFourNavItem;
  index: number;
  indexData: OneRowFourNavContent &
    Pick<OneRowFourNavProps['indexData'], 'component_id' | 'blocks_advances_id'>;
  moduleIndex: number;
} & Pick<OneRowFourNavProps, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const trackingParams = useMemo(
    () => buildTrackingParams(item, index, moduleIndex, indexData),
    [index, indexData, item, moduleIndex],
  );
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });
  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });
  const itemRef = useMergedRefs(exposureRef, validExposureRef);

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('OneRowFourNav exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(trackingParams);
    } catch (error) {
      logger.error('OneRowFourNav click tracking error:', error);
    }
  }, [onTrack, trackingParams]);

  const jumpTarget = useMemo<JumpTarget>(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.product_group?.id
        ? {
            product_group: {
              id: item.product_group.id,
              name: item.product_group.name,
              path: item.product_group.path,
            },
          }
        : {}),
      ...(item.sku?.shop_sku ? { sku: { shop_sku: item.sku.shop_sku } } : {}),
    }),
    [item],
  );
  const label = formatText(item.title);
  const imageAlt = label.replace(/\n+/g, ' ').trim();

  return (
    <div ref={itemRef} className="min-w-0 self-start">
      <MagicJump
        prefetch
        item={jumpTarget}
        onBeforeJump={handleClick}
        aria-label={imageAlt}
        className="flex min-w-0 cursor-pointer flex-col items-center justify-center gap-[4px]"
      >
        <span className="relative block size-[20px] shrink-0 overflow-hidden">
          <Image
            src={item.jump_image}
            alt={imageAlt}
            width={20}
            height={20}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="block size-full object-contain"
          />
        </span>
        <span className="line-clamp-2 min-h-[24px] w-full whitespace-pre-line break-words text-center font-br text-[10px] font-normal leading-[12px] text-primary-black">
          {label}
        </span>
      </MagicJump>
    </div>
  );
}

export default function OneRowFourNav({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: OneRowFourNavProps) {
  // 契约使用 content 包裹配置；同时兼容 Block Init 当前返回的扁平结构。
  const content = indexData.content ? { ...indexData, ...indexData.content } : indexData;
  const navItems = content.jump_target?.slice(0, MAX_NAV_ITEMS) || [];

  if (navItems.length === 0) return null;

  return (
    <section className="w-full bg-[#f7f7f7]">
      <div
        className="grid w-full py-[8px]"
        style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}
      >
        {navItems.map((item, index) => (
          <OneRowFourNavItemView
            key={item.id || index}
            item={item}
            index={index}
            indexData={content}
            moduleIndex={moduleIndex}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </div>
    </section>
  );
}
